import { Card, CardContent, CardDescription, CardHeader, CardTitle } from '@/components/ui/card';
import { Tabs, TabsContent, TabsList, TabsTrigger } from '@/components/ui/tabs';
import { ScrollArea } from '@/components/ui/scroll-area';
import { Avatar, AvatarFallback } from '@/components/ui/avatar';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { MessageSquare, Users, FolderOpen, BookOpen, GraduationCap, Send, Search, Folder, UsersRound, UserCheck, ClipboardList, Briefcase, Sparkles, Tag, CalendarDays, Building2, Library, FileText, HelpCircle, MessagesSquare, StickyNote, ShieldCheck } from 'lucide-react';
import { useGetPrototypeData } from '../hooks/useQueries';
import { useEffect, useState } from 'react';
import messagingDemo from "../Assets/Home/2.png";
import studyGroupsDemo from "../Assets/Home/3.png";
import fileManagerDemo from "../Assets/Home/4.png";
import clubDemo from "../Assets/Home/5.png";
import eLibraryPreview from "../Assets/Home/6.png";
import facultyNetworkDemo from "../Assets/Home/1.png";
import { useMemo } from 'react';

export default function PrototypesPage() {
  const { data: prototypeData, isLoading } = useGetPrototypeData();
  const [ scrollY, setScrollY ] = useState(0);

  useEffect(() => {
    const handleScroll = () => setScrollY(window.scrollY);
    window.addEventListener('scroll', handleScroll);
    return () => window.removeEventListener('scroll', handleScroll);
  }, []);

  const foldersWithCounts = useMemo(() => {
    return prototypeData?.fileManagerDemo.map(folder => ({
      name: folder,
      items: Math.floor(Math.random() * 20) + 1
    })) ?? [];
  }, [ prototypeData ]);

  const groupsWithCounts = useMemo(() => {
    return prototypeData?.groupSystemDemo.map(group => ({
      name: group,
      members: Math.floor(Math.random() * 50) + 10
    })) ?? [];
  }, [ prototypeData ]);

  const clubsWithCounts = useMemo(() => {
    return [ "Tech Club", "Robotics Society", "Literary Club", "Music & Arts" ]
      .map(club => ({
        name: club,
        members: Math.floor(Math.random() * 100) + 20
      }));
  }, []);

  const alumniList = [
    { name: 'Priya Sharma', batch: '2022', company: 'Infosys', role: 'Software Engineer' },
    { name: 'Rahul Mehta', batch: '2021', company: 'TCS', role: 'Business Analyst' },
    { name: 'Ananya Patel', batch: '2023', company: 'Wipro', role: 'Data Analyst' },
    { name: 'Karan Singh', batch: '2020', company: 'Accenture', role: 'Project Manager' },
  ];

  const attendanceSubjects = [
    { subject: 'Mathematics', percentage: 82 },
    { subject: 'Data Structures', percentage: 67 },
    { subject: 'Operating Systems', percentage: 91 },
    { subject: 'Database Management', percentage: 74 },
    { subject: 'Computer Networks', percentage: 58 },
  ];

  const placementListings = [
    { company: 'Google', role: 'Software Engineer', type: 'Full-time', mode: 'On-site' },
    { company: 'Deloitte', role: 'Business Analyst', type: 'Internship', mode: 'Hybrid' },
    { company: 'Amazon', role: 'SDE Intern', type: 'Internship', mode: 'Remote' },
    { company: 'Infosys', role: 'Systems Engineer', type: 'Full-time', mode: 'On-site' },
  ];

  const aiChat = [
    { from: 'user', text: 'Explain the difference between TCP and UDP in simple terms.' },
    { from: 'ai', text: 'TCP guarantees delivery — it confirms every packet arrived. UDP is faster but doesn\'t confirm — great for video calls where a dropped frame is fine but delay isn\'t.' },
    { from: 'user', text: 'Generate 2 mock exam questions on this topic.' },
    { from: 'ai', text: '1. What is the purpose of the three-way handshake in TCP?\n2. Name two real-world applications that prefer UDP over TCP and explain why.' },
  ];

  const subjectsList = [
    { code: 'CS301', name: 'Data Structures' },
    { code: 'MTH204', name: 'Linear Algebra' },
    { code: 'PHY101', name: 'Mechanics' },
    { code: 'ENG150', name: 'Technical Writing' },
  ];

  const timetablePeriods = [
    { time: '9:00 – 10:00', subject: 'Data Structures', teacher: 'Prof. Sharma' },
    { time: '10:00 – 11:00', subject: 'Linear Algebra', teacher: 'Prof. Iyer' },
    { time: '11:15 – 12:15', subject: 'Mechanics', teacher: '—' },
    { time: '1:00 – 2:00', subject: 'Technical Writing', teacher: 'Prof. Rao' },
  ];

  const departmentsList = [
    { name: 'B.Sc Computer Science, Batch 2024', members: 142 },
    { name: 'B.Com, Batch 2023', members: 98 },
    { name: 'B.A Economics, Batch 2024', members: 76 },
  ];

  const campusBooks = [
    { title: 'Introduction to Algorithms', author: 'Cormen et al.', available: 3 },
    { title: 'Clean Code', author: 'Robert C. Martin', available: 1 },
    { title: 'Physics for Scientists', author: 'Serway', available: 0 },
  ];

  const documentTemplates = [
    { name: 'Bonafide Certificate', access: 'Open' },
    { name: 'Semester Marksheet', access: 'Restricted' },
    { name: 'ID Card', access: 'Open' },
  ];

  const guideChat = [
    { from: 'user', text: 'When does exam registration close?' },
    { from: 'ai', text: 'Registration closes February 14 — per the latest update published by your Admin.' },
  ];

  const superGroupsList = [
    { name: 'CS Dept Announcements', type: 'Super Group', members: 320 },
    { name: 'Hostel Block A', type: 'Group', members: 45 },
    { name: 'Placement Updates 2025', type: 'Super Group', members: 210 },
  ];

  const notesList = [
    { title: 'Unit 3 — Sorting Algorithms', subject: 'Data Structures', by: 'Prof. Sharma' },
    { title: 'Midterm Revision Notes', subject: 'Linear Algebra', by: 'Priya S.' },
    { title: 'Lab Manual', subject: 'Mechanics', by: 'Prof. Iyer' },
  ];

  const rolesList = [
    { role: 'Super Admin', desc: 'Full control, manages Admin accounts' },
    { role: 'Admin', desc: 'Manages students, faculty, and institution settings' },
    { role: 'Sub-Admin', desc: 'Scoped management access' },
    { role: 'Faculty', desc: 'Classrooms, attendance, and content' },
    { role: 'Librarian', desc: 'Campus Library catalog only' },
  ];



  return (
    <div className="flex flex-col ">
      {/* Hero Section */}
      <section className="relative overflow-hidden bg-gradient-to-br from-brand-yellow via-brand-yellow to-[#FFE566] md:py-16 py-12 bg-brand-yellow py-20">
        <div
          className="absolute inset-0 opacity-10"
          style={{
            transform: `translateY(${scrollY * 0.5}px)`,
            backgroundImage: 'radial-gradient(circle, #004CFF 1px, transparent 1px)',
            backgroundSize: '50px 50px',
          }}
        />
        <div className="container relative z-10">
          <div className="mx-auto max-w-3xl text-center">
            <h1 className="mb-6 bg-gradient-to-r from-brand-blue to-[#0066FF] bg-clip-text text-5xl font-extrabold tracking-tight text-brand-black md:text-6xl">
              Feature Prototypes
            </h1>
            <p className="text-xl font-medium text-brand-black md:text-2xl">
              Explore interactive demos of UNILinkd's core features
            </p>
          </div>
        </div>
      </section>

      {/* Prototypes Section */}
      <section
        className="relative bg-brand-white py-20"
        style={{
          transform: `translateY(${scrollY * 0.1}px)`,
        }}
      >
        <div className="container">
          <Tabs defaultValue="messaging" className="w-full">
            <div className="mb-10 rounded-2xl bg-brand-yellow p-1.5 shadow-lg">
              <TabsList className="h-auto w-full flex-wrap gap-1.5 bg-transparent lg:h-auto lg:w-full lg:flex-wrap lg:justify-start lg:p-0">
                <TabsTrigger value="messaging" className="basis-[calc(50%-3px)] md:basis-[calc(20%-6px)] shrink-0 grow rounded-xl py-2.5 font-bold text-brand-black transition-all data-[state=active]:bg-brand-white data-[state=active]:text-brand-blue data-[state=active]:shadow-lg">
                  <MessageSquare className="mr-2 h-4 w-4" />
                  <span className="hidden sm:inline">Messaging</span>
                </TabsTrigger>
                <TabsTrigger value="groups" className="basis-[calc(50%-3px)] md:basis-[calc(20%-6px)] shrink-0 grow rounded-xl py-2.5 font-bold text-brand-black transition-all data-[state=active]:bg-brand-white data-[state=active]:text-brand-blue data-[state=active]:shadow-lg">
                  <Users className="mr-2 h-4 w-4" />
                  <span className="hidden sm:inline">Groups</span>
                </TabsTrigger>
                <TabsTrigger value="files" className="basis-[calc(50%-3px)] md:basis-[calc(20%-6px)] shrink-0 grow rounded-xl py-2.5 font-bold text-brand-black transition-all data-[state=active]:bg-brand-white data-[state=active]:text-brand-blue data-[state=active]:shadow-lg">
                  <FolderOpen className="mr-2 h-4 w-4" />
                  <span className="hidden sm:inline">Files</span>
                </TabsTrigger>
                <TabsTrigger value="library" className="basis-[calc(50%-3px)] md:basis-[calc(20%-6px)] shrink-0 grow rounded-xl py-2.5 font-bold text-brand-black transition-all data-[state=active]:bg-brand-white data-[state=active]:text-brand-blue data-[state=active]:shadow-lg">
                  <BookOpen className="mr-2 h-4 w-4" />
                  <span className="hidden sm:inline">Library</span>
                </TabsTrigger>
                <TabsTrigger value="faculty" className="basis-[calc(50%-3px)] md:basis-[calc(20%-6px)] shrink-0 grow rounded-xl py-2.5 font-bold text-brand-black transition-all data-[state=active]:bg-brand-white data-[state=active]:text-brand-blue data-[state=active]:shadow-lg">
                  <GraduationCap className="mr-2 h-4 w-4" />
                  <span className="hidden sm:inline">Faculty</span>
                </TabsTrigger>
                <TabsTrigger value="clubs" className="basis-[calc(50%-3px)] md:basis-[calc(20%-6px)] shrink-0 grow rounded-xl py-2.5 font-bold text-brand-black transition-all data-[state=active]:bg-brand-white data-[state=active]:text-brand-blue data-[state=active]:shadow-lg">
                  <UsersRound className="mr-2 h-4 w-4" />
                  <span className="hidden sm:inline">Clubs</span>
                </TabsTrigger>
                <TabsTrigger value="attendance" className="basis-[calc(50%-3px)] md:basis-[calc(20%-6px)] shrink-0 grow rounded-xl py-2.5 font-bold text-brand-black transition-all data-[state=active]:bg-brand-white data-[state=active]:text-brand-blue data-[state=active]:shadow-lg">
                  <ClipboardList className="mr-2 h-4 w-4" />
                  <span className="hidden sm:inline">Attendance</span>
                </TabsTrigger>
                <TabsTrigger value="placement" className="basis-[calc(50%-3px)] md:basis-[calc(20%-6px)] shrink-0 grow rounded-xl py-2.5 font-bold text-brand-black transition-all data-[state=active]:bg-brand-white data-[state=active]:text-brand-blue data-[state=active]:shadow-lg">
                  <Briefcase className="mr-2 h-4 w-4" />
                  <span className="hidden sm:inline">Placements</span>
                </TabsTrigger>
                <TabsTrigger value="alumni" className="basis-[calc(50%-3px)] md:basis-[calc(20%-6px)] shrink-0 grow rounded-xl py-2.5 font-bold text-brand-black transition-all data-[state=active]:bg-brand-white data-[state=active]:text-brand-blue data-[state=active]:shadow-lg">
                  <UserCheck className="mr-2 h-4 w-4" />
                  <span className="hidden sm:inline">Alumni</span>
                </TabsTrigger>
                <TabsTrigger value="uniai" className="basis-[calc(50%-3px)] md:basis-[calc(20%-6px)] shrink-0 grow rounded-xl py-2.5 font-bold text-brand-black transition-all data-[state=active]:bg-brand-white data-[state=active]:text-brand-blue data-[state=active]:shadow-lg">
                  <Sparkles className="mr-2 h-4 w-4" />
                  <span className="hidden sm:inline">UniAI</span>
                </TabsTrigger>
                <TabsTrigger value="subjects" className="basis-[calc(50%-3px)] md:basis-[calc(20%-6px)] shrink-0 grow rounded-xl py-2.5 font-bold text-brand-black transition-all data-[state=active]:bg-brand-white data-[state=active]:text-brand-blue data-[state=active]:shadow-lg">
                  <Tag className="mr-2 h-4 w-4" />
                  <span className="hidden sm:inline">Subjects</span>
                </TabsTrigger>
                <TabsTrigger value="classrooms" className="basis-[calc(50%-3px)] md:basis-[calc(20%-6px)] shrink-0 grow rounded-xl py-2.5 font-bold text-brand-black transition-all data-[state=active]:bg-brand-white data-[state=active]:text-brand-blue data-[state=active]:shadow-lg">
                  <CalendarDays className="mr-2 h-4 w-4" />
                  <span className="hidden sm:inline">Classrooms</span>
                </TabsTrigger>
                <TabsTrigger value="departments" className="basis-[calc(50%-3px)] md:basis-[calc(20%-6px)] shrink-0 grow rounded-xl py-2.5 font-bold text-brand-black transition-all data-[state=active]:bg-brand-white data-[state=active]:text-brand-blue data-[state=active]:shadow-lg">
                  <Building2 className="mr-2 h-4 w-4" />
                  <span className="hidden sm:inline">Departments</span>
                </TabsTrigger>
                <TabsTrigger value="campus-library" className="basis-[calc(50%-3px)] md:basis-[calc(20%-6px)] shrink-0 grow rounded-xl py-2.5 font-bold text-brand-black transition-all data-[state=active]:bg-brand-white data-[state=active]:text-brand-blue data-[state=active]:shadow-lg">
                  <Library className="mr-2 h-4 w-4" />
                  <span className="hidden sm:inline">Campus Library</span>
                </TabsTrigger>
                <TabsTrigger value="documents" className="basis-[calc(50%-3px)] md:basis-[calc(20%-6px)] shrink-0 grow rounded-xl py-2.5 font-bold text-brand-black transition-all data-[state=active]:bg-brand-white data-[state=active]:text-brand-blue data-[state=active]:shadow-lg">
                  <FileText className="mr-2 h-4 w-4" />
                  <span className="hidden sm:inline">Documents</span>
                </TabsTrigger>
                <TabsTrigger value="ai-guide" className="basis-[calc(50%-3px)] md:basis-[calc(20%-6px)] shrink-0 grow rounded-xl py-2.5 font-bold text-brand-black transition-all data-[state=active]:bg-brand-white data-[state=active]:text-brand-blue data-[state=active]:shadow-lg">
                  <HelpCircle className="mr-2 h-4 w-4" />
                  <span className="hidden sm:inline">AI-Guide</span>
                </TabsTrigger>
                <TabsTrigger value="groups-channels" className="basis-[calc(50%-3px)] md:basis-[calc(20%-6px)] shrink-0 grow rounded-xl py-2.5 font-bold text-brand-black transition-all data-[state=active]:bg-brand-white data-[state=active]:text-brand-blue data-[state=active]:shadow-lg">
                  <MessagesSquare className="mr-2 h-4 w-4" />
                  <span className="hidden sm:inline">Groups & Channels</span>
                </TabsTrigger>
                <TabsTrigger value="notes" className="basis-[calc(50%-3px)] md:basis-[calc(20%-6px)] shrink-0 grow rounded-xl py-2.5 font-bold text-brand-black transition-all data-[state=active]:bg-brand-white data-[state=active]:text-brand-blue data-[state=active]:shadow-lg">
                  <StickyNote className="mr-2 h-4 w-4" />
                  <span className="hidden sm:inline">Notes</span>
                </TabsTrigger>
                <TabsTrigger value="access-control" className="basis-[calc(50%-3px)] md:basis-[calc(20%-6px)] shrink-0 grow rounded-xl py-2.5 font-bold text-brand-black transition-all data-[state=active]:bg-brand-white data-[state=active]:text-brand-blue data-[state=active]:shadow-lg">
                  <ShieldCheck className="mr-2 h-4 w-4" />
                  <span className="hidden sm:inline">Access Control</span>
                </TabsTrigger>
              </TabsList>
            </div>

            {/* Messaging Demo */}
            <TabsContent value="messaging">
              <div className="grid gap-8 lg:grid-cols-2">
                <div>
                  <h3 className="mb-4 bg-gradient-to-r from-brand-blue to-[#0066FF] bg-clip-text text-3xl font-extrabold text-brand-black">Messaging System</h3>
                  <p className="mb-6 text-lg font-medium text-brand-black">
                    Real-time messaging with peers and faculty. Send text, files, and media instantly.
                  </p>
                  <div className="relative">
                    <div className="absolute -inset-4 rounded-3xl bg-gradient-to-r from-brand-blue to-[#0066FF] opacity-20 blur-2xl"></div>
                    <img
                      src={messagingDemo}
                      alt="Messaging Demo"
                      className="relative rounded-3xl shadow-2xl"
                    />
                  </div>
                </div>
                <Card className="rounded-3xl bg-brand-white shadow-xl">
                  <CardHeader>
                    <CardTitle className="bg-gradient-to-r from-brand-blue to-[#0066FF] bg-clip-text text-xl font-bold text-brand-black">Chat Preview</CardTitle>
                    <CardDescription className="font-medium text-brand-black">Interactive messaging interface</CardDescription>
                  </CardHeader>
                  <CardContent>
                    <ScrollArea className="h-[400px] rounded-2xl bg-gray-50 p-4 shadow-inner">
                      <div className="space-y-4">
                        {prototypeData?.messagingDemo.map((message, index) => (
                          <div key={index} className={`flex ${index % 2 === 0 ? 'justify-start' : 'justify-end'}`}>
                            <div className={`flex max-w-[80%] items-start space-x-2 ${index % 2 === 0 ? '' : 'flex-row-reverse space-x-reverse'}`}>
                              <Avatar className="h-8 w-8 shadow-md">
                                <AvatarFallback className="bg-brand-yellow font-bold text-brand-black">{index % 2 === 0 ? 'U' : 'M'}</AvatarFallback>
                              </Avatar>
                              <div className={`rounded-2xl px-4 py-2 font-medium shadow-md ${index % 2 === 0 ? 'bg-brand-white text-brand-black' : 'bg-gradient-to-r from-brand-blue to-[#0066FF] text-brand-white'}`}>
                                <p className="text-sm">{message}</p>
                              </div>
                            </div>
                          </div>
                        ))}
                      </div>
                    </ScrollArea>
                    <div className="mt-4 flex space-x-2">
                      <Input placeholder="Type a message..." className="rounded-xl font-medium shadow-md" />
                      <Button size="icon" className="rounded-xl bg-gradient-to-r from-brand-blue to-[#0066FF] shadow-lg">
                        <Send className="h-4 w-4" />
                      </Button>
                    </div>
                  </CardContent>
                </Card>
              </div>
            </TabsContent>

            {/* Groups Demo */}
            <TabsContent value="groups">
              <div className="grid gap-8 lg:grid-cols-2">
                <div>
                  <h3 className="mb-4 bg-gradient-to-r from-brand-blue to-[#0066FF] bg-clip-text text-3xl font-extrabold text-brand-black">Study Groups</h3>
                  <p className="mb-6 text-lg font-medium text-brand-black">
                    Create and join study groups for collaborative learning. Share resources and discuss topics.
                  </p>
                  <div className="relative">
                    <div className="absolute -inset-4 rounded-3xl bg-gradient-to-r from-brand-blue to-[#0066FF] opacity-20 blur-2xl"></div>
                    <img
                      src={studyGroupsDemo}
                      alt="Study Groups"
                      className="relative rounded-3xl shadow-2xl"
                    />
                  </div>
                </div>
                <Card className="rounded-3xl bg-brand-white shadow-xl">
                  <CardHeader>
                    <CardTitle className="bg-gradient-to-r from-brand-blue to-[#0066FF] bg-clip-text text-xl font-bold text-brand-black">Available Groups</CardTitle>
                    <CardDescription className="font-medium text-brand-black">Join a study group or create your own</CardDescription>
                  </CardHeader>
                  <CardContent>
                    <div className="space-y-4">
                      {prototypeData?.groupSystemDemo.map((group, index) => (
                        <Card key={index} className="rounded-2xl bg-brand-white shadow-lg transition-all hover:scale-105 hover:shadow-xl">
                          <CardHeader>
                            <div className="flex items-center justify-between">
                              <div className="flex items-center space-x-3">
                                <div className="flex h-10 w-10 items-center justify-center rounded-xl bg-gradient-to-br from-brand-blue to-[#0066FF] shadow-md">
                                  <Users className="h-5 w-5 text-brand-white" />
                                </div>
                                <div>
                                  <CardTitle className="text-base font-bold text-brand-black">{group}</CardTitle>
                                  <CardDescription className="text-xs font-medium text-brand-black">
                                    {groupsWithCounts[ index ].members} members
                                  </CardDescription>
                                </div>
                              </div>
                              <Button size="sm" className="rounded-xl bg-gradient-to-r from-brand-blue to-[#0066FF] font-bold shadow-lg">Join</Button>
                            </div>
                          </CardHeader>
                        </Card>
                      ))}
                    </div>
                  </CardContent>
                </Card>
              </div>
            </TabsContent>

            {/* Files Demo */}
            <TabsContent value="files">
              <div className="grid gap-8 lg:grid-cols-2">
                <div>
                  <h3 className="mb-4 bg-gradient-to-r from-brand-blue to-[#0066FF] bg-clip-text text-3xl font-extrabold text-brand-black">File Manager</h3>
                  <p className="mb-6 text-lg font-medium text-brand-black">
                    Organize your notes, assignments, and study materials in a structured file system.
                  </p>
                  <div className="relative">
                    <div className="absolute -inset-4 rounded-3xl bg-gradient-to-r from-brand-blue to-[#0066FF] opacity-20 blur-2xl"></div>
                    <img
                      src={fileManagerDemo}
                      alt="File Manager"
                      className="relative rounded-3xl shadow-2xl"
                    />
                  </div>
                </div>
                <Card className="rounded-3xl bg-brand-white shadow-xl">
                  <CardHeader>
                    <CardTitle className="bg-gradient-to-r from-brand-blue to-[#0066FF] bg-clip-text text-xl font-bold text-brand-black">My Files</CardTitle>
                    <CardDescription className="font-medium text-brand-black">Browse and manage your documents</CardDescription>
                  </CardHeader>
                  <CardContent>
                    <div className="space-y-2">
                      {prototypeData?.fileManagerDemo.map((folder, index) => (
                        <div
                          key={index}
                          className="flex items-center space-x-3 rounded-2xl bg-brand-white p-3 shadow-lg transition-all hover:scale-105 hover:shadow-xl"
                        >
                          <Folder className="h-5 w-5 text-brand-blue" />
                          <div className="flex-1">
                            <p className="font-bold text-brand-black">{folder}</p>
                            <p className="text-xs font-medium text-brand-black">
                              {foldersWithCounts[ index ].items} items
                            </p>
                          </div>
                        </div>
                      ))}
                    </div>
                  </CardContent>
                </Card>
              </div>
            </TabsContent>

            {/* Library Demo */}
            <TabsContent value="library">
              <div className="grid gap-8 lg:grid-cols-2">
                <div>
                  <h3 className="mb-4 bg-gradient-to-r from-brand-blue to-[#0066FF] bg-clip-text text-3xl font-extrabold text-brand-black">E-Library</h3>
                  <p className="mb-6 text-lg font-medium text-brand-black">
                    Access thousands of educational books and resources. Search, bookmark, and read offline.
                  </p>
                  <div className="relative">
                    <div className="absolute -inset-4 rounded-3xl bg-gradient-to-r from-brand-blue to-[#0066FF] opacity-20 blur-2xl"></div>
                    <img
                      src={eLibraryPreview}
                      alt="E-Library"
                      className="relative rounded-3xl shadow-2xl"
                    />
                  </div>
                </div>
                <Card className="rounded-3xl bg-brand-white shadow-xl">
                  <CardHeader>
                    <CardTitle className="bg-gradient-to-r from-brand-blue to-[#0066FF] bg-clip-text text-xl font-bold text-brand-black">Library Catalog</CardTitle>
                    <CardDescription className="font-medium text-brand-black">Browse available books and resources</CardDescription>
                  </CardHeader>
                  <CardContent>
                    <div className="mb-4">
                      <div className="relative">
                        <Search className="absolute left-3 top-1/2 h-4 w-4 -translate-y-1/2 text-brand-black" />
                        <Input placeholder="Search books..." className="rounded-xl pl-9 font-medium shadow-md" />
                      </div>
                    </div>
                    <div className="space-y-3">
                      {prototypeData?.libraryPreviewDemo.map((book, index) => (
                        <Card key={index} className="rounded-2xl bg-brand-white shadow-lg transition-all hover:scale-105 hover:shadow-xl">
                          <CardHeader>
                            <div className="flex items-center justify-between">
                              <div className="flex items-center space-x-3">
                                <div className="flex h-10 w-10 items-center justify-center rounded-xl bg-gradient-to-br from-brand-blue to-[#0066FF] shadow-md">
                                  <BookOpen className="h-5 w-5 text-brand-white" />
                                </div>
                                <div>
                                  <CardTitle className="text-sm font-bold text-brand-black">{book}</CardTitle>
                                  <CardDescription className="text-xs font-medium text-brand-black">Available</CardDescription>
                                </div>
                              </div>
                              <Button size="sm" variant="outline" className="rounded-xl font-bold shadow-md">View</Button>
                            </div>
                          </CardHeader>
                        </Card>
                      ))}
                    </div>
                  </CardContent>
                </Card>
              </div>
            </TabsContent>

            {/* Faculty Demo */}
            <TabsContent value="faculty">
              <div className="grid gap-8 lg:grid-cols-2">
                <div>
                  <h3 className="mb-4 bg-gradient-to-r from-brand-blue to-[#0066FF] bg-clip-text text-3xl font-extrabold text-brand-black">Faculty Network</h3>
                  <p className="mb-6 text-lg font-medium text-brand-black">
                    Connect with faculty members, schedule office hours, and get academic guidance.
                  </p>
                  <div className="relative">
                    <div className="absolute -inset-4 rounded-3xl bg-gradient-to-r from-brand-blue to-[#0066FF] opacity-20 blur-2xl"></div>
                    <img
                      src={facultyNetworkDemo}
                      alt="Faculty Network"
                      className="relative rounded-3xl shadow-2xl"
                    />
                  </div>
                </div>
                <Card className="rounded-3xl bg-brand-white shadow-xl">
                  <CardHeader>
                    <CardTitle className="bg-gradient-to-r from-brand-blue to-[#0066FF] bg-clip-text text-xl font-bold text-brand-black">Faculty Directory</CardTitle>
                    <CardDescription className="font-medium text-brand-black">Connect with your professors/teachers</CardDescription>
                  </CardHeader>
                  <CardContent>
                    <div className="space-y-4">
                      {prototypeData?.facultyConnectionDemo.map((faculty, index) => (
                        <Card key={index} className="rounded-2xl bg-brand-white shadow-lg transition-all hover:scale-105 hover:shadow-xl">
                          <CardHeader>
                            <div className="flex items-center justify-between">
                              <div className="flex items-center space-x-3">
                                <Avatar className="h-10 w-10 shadow-md">
                                  <AvatarFallback
                                    className="
                                      flex items-center justify-center
                                      bg-brand-yellow
                                      font-bold text-brand-black
                                      text-sm
                                      leading-none
                                    "
                                  >
                                    {faculty
                                      .split(" ")
                                      .map((n) => n[ 0 ])
                                      .join("")
                                      .slice(0, 2)
                                      .toUpperCase()}
                                  </AvatarFallback>
                                </Avatar>

                                <div>
                                  <CardTitle className="text-base font-bold text-brand-black">{faculty}</CardTitle>
                                  <CardDescription className="text-xs font-medium text-brand-black">Available</CardDescription>
                                </div>
                              </div>
                              <Button size="sm" className="rounded-xl bg-gradient-to-r from-brand-blue to-[#0066FF] font-bold shadow-lg">Connect</Button>
                            </div>
                          </CardHeader>
                        </Card>
                      ))}
                    </div>
                  </CardContent>
                </Card>
              </div>
            </TabsContent>

            {/* Clubs Demo */}
            <TabsContent value="clubs">
              <div className="grid gap-8 lg:grid-cols-2">
                <div>
                  <h3 className="mb-4 bg-gradient-to-r from-brand-blue to-[#0066FF] bg-clip-text
                     text-3xl font-extrabold text-brand-black">
                    Student Clubs & Communities
                  </h3>
                  <p className="mb-6 text-lg font-medium text-brand-black">
                    Join official clubs, interest groups, and student communities.
                    Participate in events, discussions, and announcements.
                  </p>
                  <div className="relative">
                    <div className="absolute -inset-4 rounded-3xl bg-gradient-to-r from-brand-blue to-[#0066FF] opacity-20 blur-2xl"></div>
                    <img
                      src={clubDemo}
                      alt="Faculty Network"
                      className="relative rounded-3xl shadow-2xl"
                    />
                  </div>
                </div>

                <Card className="rounded-3xl bg-brand-white shadow-xl">
                  <CardHeader>
                    <CardTitle className="bg-gradient-to-r from-brand-blue to-[#0066FF]
                              bg-clip-text text-xl font-bold text-brand-black">
                      Active Clubs
                    </CardTitle>
                    <CardDescription className="font-medium text-brand-black">
                      Discover and join campus communities
                    </CardDescription>
                  </CardHeader>

                  <CardContent className="space-y-4">
                    {[ "Tech Club", "Robotics Society", "Literary Club", "Music & Arts" ].map(
                      (club, index) => (
                        <Card
                          key={index}
                          className="rounded-2xl bg-brand-white shadow-lg transition-all
                         hover:scale-105 hover:shadow-xl"
                        >
                          <CardHeader>
                            <div className="flex items-center justify-between">
                              <div className="flex items-center space-x-3">
                                <div className="flex h-10 w-10 items-center justify-center
                                    rounded-xl bg-gradient-to-br
                                    from-brand-blue to-[#0066FF] shadow-md">
                                  <UsersRound className="h-5 w-5 text-brand-white" />
                                </div>
                                <div>
                                  <CardTitle className="text-base font-bold text-brand-black">
                                    {club}
                                  </CardTitle>
                                  <CardDescription className="text-xs font-medium text-brand-black">
                                    {clubsWithCounts[ index ].members} members
                                  </CardDescription>
                                </div>
                              </div>
                              <Button size="sm" className="rounded-xl bg-gradient-to-r
                                               from-brand-blue to-[#0066FF]
                                               font-bold shadow-lg">
                                Join
                              </Button>
                            </div>
                          </CardHeader>
                        </Card>
                      )
                    )}
                  </CardContent>
                </Card>
              </div>
            </TabsContent>

            {/* Attendance Demo */}
            <TabsContent value="attendance">
              <div className="grid gap-8 lg:grid-cols-2">
                <div>
                  <h3 className="mb-4 bg-gradient-to-r from-brand-blue to-[#0066FF] bg-clip-text text-3xl font-extrabold text-brand-black">Student Attendance</h3>
                  <p className="mb-6 text-lg font-medium text-brand-black">
                    Faculty submit attendance from the app — students see their subject-wise percentage updated in real time. No registers, no office visits.
                  </p>
                  <div className="relative">
                    <div className="absolute -inset-4 rounded-3xl bg-gradient-to-r from-brand-blue to-[#0066FF] opacity-20 blur-2xl"></div>
                    <img src={fileManagerDemo} alt="Attendance" className="relative rounded-3xl shadow-2xl" />
                  </div>
                </div>
                <Card className="rounded-3xl bg-brand-white shadow-xl">
                  <CardHeader>
                    <CardTitle className="bg-gradient-to-r from-brand-blue to-[#0066FF] bg-clip-text text-xl font-bold text-brand-black">My Attendance Report</CardTitle>
                    <CardDescription className="font-medium text-brand-black">Subject-wise breakdown — updated after every session</CardDescription>
                  </CardHeader>
                  <CardContent className="space-y-5">
                    {attendanceSubjects.map((item, i) => (
                      <div key={i}>
                        <div className="mb-1.5 flex items-center justify-between">
                          <span className="text-sm font-bold text-brand-black">{item.subject}</span>
                          <span className={`text-sm font-extrabold ${item.percentage >= 75 ? 'text-green-600' : 'text-red-500'}`}>
                            {item.percentage}%
                          </span>
                        </div>
                        <div className="h-2.5 w-full rounded-full bg-gray-100">
                          <div
                            className={`h-2.5 rounded-full transition-all ${item.percentage >= 75 ? 'bg-gradient-to-r from-green-400 to-green-600' : 'bg-gradient-to-r from-red-400 to-red-500'}`}
                            style={{ width: `${item.percentage}%` }}
                          />
                        </div>
                        {item.percentage < 75 && (
                          <p className="mt-1 text-xs font-medium text-red-500">Below 75% — attendance shortage</p>
                        )}
                      </div>
                    ))}
                    <div className="mt-4 rounded-2xl bg-brand-yellow/20 px-4 py-3">
                      <p className="text-xs font-bold text-brand-black">Overall: <span className="text-brand-blue">74.4%</span> across all subjects</p>
                    </div>
                  </CardContent>
                </Card>
              </div>
            </TabsContent>

            {/* Placement Demo */}
            <TabsContent value="placement">
              <div className="grid gap-8 lg:grid-cols-2">
                <div>
                  <h3 className="mb-4 bg-gradient-to-r from-brand-blue to-[#0066FF] bg-clip-text text-3xl font-extrabold text-brand-black">Placement Cell</h3>
                  <p className="mb-6 text-lg font-medium text-brand-black">
                    Your institution's managed job board. Admins post verified listings, students browse and apply directly from the app, and track every application.
                  </p>
                  <div className="relative">
                    <div className="absolute -inset-4 rounded-3xl bg-gradient-to-r from-brand-blue to-[#0066FF] opacity-20 blur-2xl"></div>
                    <img src={studyGroupsDemo} alt="Placement" className="relative rounded-3xl shadow-2xl" />
                  </div>
                </div>
                <Card className="rounded-3xl bg-brand-white shadow-xl">
                  <CardHeader>
                    <CardTitle className="bg-gradient-to-r from-brand-blue to-[#0066FF] bg-clip-text text-xl font-bold text-brand-black">Active Listings</CardTitle>
                    <CardDescription className="font-medium text-brand-black">Verified opportunities from your placement team</CardDescription>
                  </CardHeader>
                  <CardContent className="space-y-3">
                    {placementListings.map((job, i) => (
                      <Card key={i} className="rounded-2xl bg-brand-white shadow-lg transition-all hover:scale-[1.02] hover:shadow-xl">
                        <CardHeader className="pb-3">
                          <div className="flex items-center justify-between">
                            <div className="flex items-center space-x-3">
                              <div className="flex h-10 w-10 items-center justify-center rounded-xl bg-gradient-to-br from-brand-blue to-[#0066FF] shadow-md">
                                <Briefcase className="h-5 w-5 text-brand-white" />
                              </div>
                              <div>
                                <CardTitle className="text-sm font-bold text-brand-black">{job.company}</CardTitle>
                                <CardDescription className="text-xs font-medium text-brand-black">{job.role}</CardDescription>
                              </div>
                            </div>
                            <div className="flex flex-col items-end gap-1">
                              <span className={`rounded-full px-2 py-0.5 text-[10px] font-bold ${job.type === 'Internship' ? 'bg-brand-yellow text-brand-black' : 'bg-brand-blue/10 text-brand-blue'}`}>
                                {job.type}
                              </span>
                              <Button size="sm" className="h-6 rounded-xl bg-gradient-to-r from-brand-blue to-[#0066FF] px-3 text-xs font-bold shadow-lg">Apply</Button>
                            </div>
                          </div>
                        </CardHeader>
                      </Card>
                    ))}
                  </CardContent>
                </Card>
              </div>
            </TabsContent>

            {/* Alumni Demo */}
            <TabsContent value="alumni">
              <div className="grid gap-8 lg:grid-cols-2">
                <div>
                  <h3 className="mb-4 bg-gradient-to-r from-brand-blue to-[#0066FF] bg-clip-text text-3xl font-extrabold text-brand-black">Alumni Programme</h3>
                  <p className="mb-6 text-lg font-medium text-brand-black">
                    Maintain a living directory of your graduates. Track career outcomes, log employment updates, and keep your institution's alumni network growing.
                  </p>
                  <div className="relative">
                    <div className="absolute -inset-4 rounded-3xl bg-gradient-to-r from-brand-blue to-[#0066FF] opacity-20 blur-2xl"></div>
                    <img src={facultyNetworkDemo} alt="Alumni" className="relative rounded-3xl shadow-2xl" />
                  </div>
                </div>
                <Card className="rounded-3xl bg-brand-white shadow-xl">
                  <CardHeader>
                    <CardTitle className="bg-gradient-to-r from-brand-blue to-[#0066FF] bg-clip-text text-xl font-bold text-brand-black">Alumni Directory</CardTitle>
                    <CardDescription className="font-medium text-brand-black">Former students — staying connected</CardDescription>
                  </CardHeader>
                  <CardContent className="space-y-3">
                    {alumniList.map((alumni, i) => (
                      <Card key={i} className="rounded-2xl bg-brand-white shadow-lg transition-all hover:scale-[1.02] hover:shadow-xl">
                        <CardHeader className="pb-3">
                          <div className="flex items-center justify-between">
                            <div className="flex items-center space-x-3">
                              <div className="flex h-10 w-10 items-center justify-center rounded-xl bg-gradient-to-br from-brand-yellow to-[#FFE566] shadow-md">
                                <span className="text-sm font-extrabold text-brand-black">
                                  {alumni.name.split(' ').map(n => n[0]).join('')}
                                </span>
                              </div>
                              <div>
                                <CardTitle className="text-sm font-bold text-brand-black">{alumni.name}</CardTitle>
                                <CardDescription className="text-xs font-medium text-brand-black">Batch {alumni.batch} · {alumni.role}</CardDescription>
                              </div>
                            </div>
                            <div className="text-right">
                              <p className="text-xs font-bold text-brand-blue">{alumni.company}</p>
                              <Button size="sm" variant="outline" className="mt-1 h-6 rounded-xl px-3 text-xs font-bold shadow-md">View</Button>
                            </div>
                          </div>
                        </CardHeader>
                      </Card>
                    ))}
                  </CardContent>
                </Card>
              </div>
            </TabsContent>

            {/* UniAI Demo */}
            <TabsContent value="uniai">
              <div className="grid gap-8 lg:grid-cols-2">
                <div>
                  <h3 className="mb-4 bg-gradient-to-r from-brand-blue to-[#0066FF] bg-clip-text text-3xl font-extrabold text-brand-black">UniAI — Built-in AI</h3>
                  <p className="mb-6 text-lg font-medium text-brand-black">
                    AI assistance built into the app for every user. Students get study help, mock exams, and concept explanations. Faculty draft content and announcements.
                  </p>
                  <div className="relative">
                    <div className="absolute -inset-4 rounded-3xl bg-gradient-to-r from-brand-blue to-[#0066FF] opacity-20 blur-2xl"></div>
                    <img src={eLibraryPreview} alt="UniAI" className="relative rounded-3xl shadow-2xl" />
                  </div>
                </div>
                <Card className="rounded-3xl bg-brand-white shadow-xl">
                  <CardHeader>
                    <CardTitle className="bg-gradient-to-r from-brand-blue to-[#0066FF] bg-clip-text text-xl font-bold text-brand-black">UniAI Chat</CardTitle>
                    <CardDescription className="font-medium text-brand-black">Ask anything — study help, exam prep, content creation</CardDescription>
                  </CardHeader>
                  <CardContent>
                    <ScrollArea className="h-[340px] rounded-2xl bg-gray-50 p-4 shadow-inner">
                      <div className="space-y-4">
                        {aiChat.map((msg, i) => (
                          <div key={i} className={`flex ${msg.from === 'user' ? 'justify-end' : 'justify-start'}`}>
                            <div className={`flex max-w-[85%] items-start space-x-2 ${msg.from === 'user' ? 'flex-row-reverse space-x-reverse' : ''}`}>
                              <div className={`flex h-8 w-8 flex-shrink-0 items-center justify-center rounded-full shadow-md ${msg.from === 'ai' ? 'bg-gradient-to-br from-brand-blue to-[#0066FF]' : 'bg-brand-yellow'}`}>
                                {msg.from === 'ai'
                                  ? <Sparkles className="h-4 w-4 text-white" />
                                  : <span className="text-xs font-extrabold text-brand-black">U</span>
                                }
                              </div>
                              <div className={`rounded-2xl px-4 py-2.5 shadow-md ${msg.from === 'user' ? 'bg-gradient-to-r from-brand-blue to-[#0066FF] text-brand-white' : 'bg-brand-white text-brand-black'}`}>
                                <p className="whitespace-pre-line text-sm font-medium">{msg.text}</p>
                              </div>
                            </div>
                          </div>
                        ))}
                      </div>
                    </ScrollArea>
                    <div className="mt-4 flex space-x-2">
                      <Input placeholder="Ask UniAI anything..." className="rounded-xl font-medium shadow-md" />
                      <Button size="icon" className="rounded-xl bg-gradient-to-r from-brand-blue to-[#0066FF] shadow-lg">
                        <Send className="h-4 w-4" />
                      </Button>
                    </div>
                  </CardContent>
                </Card>
              </div>
            </TabsContent>

            {/* Subjects Demo */}
            <TabsContent value="subjects">
              <div className="grid gap-8 lg:grid-cols-2">
                <div>
                  <h3 className="mb-4 bg-gradient-to-r from-brand-blue to-[#0066FF] bg-clip-text text-3xl font-extrabold text-brand-black">Subject Registry</h3>
                  <p className="mb-6 text-lg font-medium text-brand-black">
                    One shared list of subject codes and names for the whole institution — used consistently across attendance and timetables.
                  </p>
                  <div className="relative">
                    <div className="absolute -inset-4 rounded-3xl bg-gradient-to-r from-brand-blue to-[#0066FF] opacity-20 blur-2xl"></div>
                    <img src={eLibraryPreview} alt="Subject Registry" className="relative rounded-3xl shadow-2xl" />
                  </div>
                </div>
                <Card className="rounded-3xl bg-brand-white shadow-xl">
                  <CardHeader>
                    <CardTitle className="bg-gradient-to-r from-brand-blue to-[#0066FF] bg-clip-text text-xl font-bold text-brand-black">Subject Registry</CardTitle>
                    <CardDescription className="font-medium text-brand-black">Searchable by code or name</CardDescription>
                  </CardHeader>
                  <CardContent>
                    <div className="mb-4 relative">
                      <Search className="absolute left-3 top-1/2 h-4 w-4 -translate-y-1/2 text-brand-black" />
                      <Input placeholder="Search by code or name..." className="rounded-xl pl-9 font-medium shadow-md" />
                    </div>
                    <div className="space-y-3">
                      {subjectsList.map((subj, i) => (
                        <Card key={i} className="rounded-2xl bg-brand-white shadow-lg transition-all hover:scale-105 hover:shadow-xl">
                          <CardHeader>
                            <div className="flex items-center space-x-3">
                              <div className="flex h-10 w-10 items-center justify-center rounded-xl bg-gradient-to-br from-brand-blue to-[#0066FF] shadow-md">
                                <Tag className="h-5 w-5 text-brand-white" />
                              </div>
                              <div>
                                <CardTitle className="text-sm font-bold text-brand-black">{subj.name}</CardTitle>
                                <CardDescription className="text-xs font-medium text-brand-black">Code: {subj.code}</CardDescription>
                              </div>
                            </div>
                          </CardHeader>
                        </Card>
                      ))}
                    </div>
                  </CardContent>
                </Card>
              </div>
            </TabsContent>

            {/* Classrooms Demo */}
            <TabsContent value="classrooms">
              <div className="grid gap-8 lg:grid-cols-2">
                <div>
                  <h3 className="mb-4 bg-gradient-to-r from-brand-blue to-[#0066FF] bg-clip-text text-3xl font-extrabold text-brand-black">Classrooms & Timetables</h3>
                  <p className="mb-6 text-lg font-medium text-brand-black">
                    A dedicated group chat and weekly timetable for every class batch — with Google Meet built right in. Replaces the need for separate WhatsApp groups.
                  </p>
                  <div className="relative">
                    <div className="absolute -inset-4 rounded-3xl bg-gradient-to-r from-brand-blue to-[#0066FF] opacity-20 blur-2xl"></div>
                    <img src={studyGroupsDemo} alt="Classrooms" className="relative rounded-3xl shadow-2xl" />
                  </div>
                </div>
                <Card className="rounded-3xl bg-brand-white shadow-xl">
                  <CardHeader>
                    <CardTitle className="bg-gradient-to-r from-brand-blue to-[#0066FF] bg-clip-text text-xl font-bold text-brand-black">Today's Timetable</CardTitle>
                    <CardDescription className="font-medium text-brand-black">B.Sc Computer Science, Sem 4</CardDescription>
                  </CardHeader>
                  <CardContent className="space-y-3">
                    {timetablePeriods.map((period, i) => (
                      <Card key={i} className="rounded-2xl bg-brand-white shadow-lg transition-all hover:scale-[1.02] hover:shadow-xl">
                        <CardHeader className="pb-3">
                          <div className="flex items-center justify-between">
                            <div className="flex items-center space-x-3">
                              <div className="flex h-10 w-10 items-center justify-center rounded-xl bg-gradient-to-br from-brand-blue to-[#0066FF] shadow-md">
                                <CalendarDays className="h-5 w-5 text-brand-white" />
                              </div>
                              <div>
                                <CardTitle className="text-sm font-bold text-brand-black">{period.subject}</CardTitle>
                                <CardDescription className="text-xs font-medium text-brand-black">{period.time} · {period.teacher}</CardDescription>
                              </div>
                            </div>
                          </div>
                        </CardHeader>
                      </Card>
                    ))}
                  </CardContent>
                </Card>
              </div>
            </TabsContent>

            {/* Departments Demo */}
            <TabsContent value="departments">
              <div className="grid gap-8 lg:grid-cols-2">
                <div>
                  <h3 className="mb-4 bg-gradient-to-r from-brand-blue to-[#0066FF] bg-clip-text text-3xl font-extrabold text-brand-black">Departments</h3>
                  <p className="mb-6 text-lg font-medium text-brand-black">
                    Organize students into academic batches, each with its own dedicated announcement channel. Every student belongs to exactly one.
                  </p>
                  <div className="relative">
                    <div className="absolute -inset-4 rounded-3xl bg-gradient-to-r from-brand-blue to-[#0066FF] opacity-20 blur-2xl"></div>
                    <img src={facultyNetworkDemo} alt="Departments" className="relative rounded-3xl shadow-2xl" />
                  </div>
                </div>
                <Card className="rounded-3xl bg-brand-white shadow-xl">
                  <CardHeader>
                    <CardTitle className="bg-gradient-to-r from-brand-blue to-[#0066FF] bg-clip-text text-xl font-bold text-brand-black">Departments</CardTitle>
                    <CardDescription className="font-medium text-brand-black">Select your academic batch</CardDescription>
                  </CardHeader>
                  <CardContent className="space-y-4">
                    {departmentsList.map((dept, i) => (
                      <Card key={i} className="rounded-2xl bg-brand-white shadow-lg transition-all hover:scale-105 hover:shadow-xl">
                        <CardHeader>
                          <div className="flex items-center justify-between">
                            <div className="flex items-center space-x-3">
                              <div className="flex h-10 w-10 items-center justify-center rounded-xl bg-gradient-to-br from-brand-blue to-[#0066FF] shadow-md">
                                <Building2 className="h-5 w-5 text-brand-white" />
                              </div>
                              <div>
                                <CardTitle className="text-sm font-bold text-brand-black">{dept.name}</CardTitle>
                                <CardDescription className="text-xs font-medium text-brand-black">{dept.members} members</CardDescription>
                              </div>
                            </div>
                            <Button size="sm" variant="outline" className="rounded-xl font-bold shadow-md">Join</Button>
                          </div>
                        </CardHeader>
                      </Card>
                    ))}
                  </CardContent>
                </Card>
              </div>
            </TabsContent>

            {/* Campus Library Demo */}
            <TabsContent value="campus-library">
              <div className="grid gap-8 lg:grid-cols-2">
                <div>
                  <h3 className="mb-4 bg-gradient-to-r from-brand-blue to-[#0066FF] bg-clip-text text-3xl font-extrabold text-brand-black">Campus Library</h3>
                  <p className="mb-6 text-lg font-medium text-brand-black">
                    A searchable digital catalog of your institution's physical book collection — check availability before visiting in person. Managed by the Librarian role.
                  </p>
                  <div className="relative">
                    <div className="absolute -inset-4 rounded-3xl bg-gradient-to-r from-brand-blue to-[#0066FF] opacity-20 blur-2xl"></div>
                    <img src={eLibraryPreview} alt="Campus Library" className="relative rounded-3xl shadow-2xl" />
                  </div>
                </div>
                <Card className="rounded-3xl bg-brand-white shadow-xl">
                  <CardHeader>
                    <CardTitle className="bg-gradient-to-r from-brand-blue to-[#0066FF] bg-clip-text text-xl font-bold text-brand-black">Library Catalog</CardTitle>
                    <CardDescription className="font-medium text-brand-black">Search the physical collection</CardDescription>
                  </CardHeader>
                  <CardContent className="space-y-3">
                    {campusBooks.map((book, i) => (
                      <Card key={i} className="rounded-2xl bg-brand-white shadow-lg transition-all hover:scale-[1.02] hover:shadow-xl">
                        <CardHeader className="pb-3">
                          <div className="flex items-center justify-between">
                            <div className="flex items-center space-x-3">
                              <div className="flex h-10 w-10 items-center justify-center rounded-xl bg-gradient-to-br from-brand-blue to-[#0066FF] shadow-md">
                                <Library className="h-5 w-5 text-brand-white" />
                              </div>
                              <div>
                                <CardTitle className="text-sm font-bold text-brand-black">{book.title}</CardTitle>
                                <CardDescription className="text-xs font-medium text-brand-black">{book.author}</CardDescription>
                              </div>
                            </div>
                            <span className={`rounded-full px-2 py-0.5 text-[10px] font-bold ${book.available > 0 ? 'bg-green-100 text-green-700' : 'bg-red-100 text-red-600'}`}>
                              {book.available > 0 ? `${book.available} available` : 'Checked out'}
                            </span>
                          </div>
                        </CardHeader>
                      </Card>
                    ))}
                  </CardContent>
                </Card>
              </div>
            </TabsContent>

            {/* Documents Demo */}
            <TabsContent value="documents">
              <div className="grid gap-8 lg:grid-cols-2">
                <div>
                  <h3 className="mb-4 bg-gradient-to-r from-brand-blue to-[#0066FF] bg-clip-text text-3xl font-extrabold text-brand-black">Documents & Certificates</h3>
                  <p className="mb-6 text-lg font-medium text-brand-black">
                    Design certificates, ID cards, and letters that auto-fill each person's own name, email, and ID. Students self-serve download their own copy.
                  </p>
                  <div className="relative">
                    <div className="absolute -inset-4 rounded-3xl bg-gradient-to-r from-brand-blue to-[#0066FF] opacity-20 blur-2xl"></div>
                    <img src={fileManagerDemo} alt="Documents" className="relative rounded-3xl shadow-2xl" />
                  </div>
                </div>
                <Card className="rounded-3xl bg-brand-white shadow-xl">
                  <CardHeader>
                    <CardTitle className="bg-gradient-to-r from-brand-blue to-[#0066FF] bg-clip-text text-xl font-bold text-brand-black">Your Documents</CardTitle>
                    <CardDescription className="font-medium text-brand-black">Self-serve downloads from the Library tab</CardDescription>
                  </CardHeader>
                  <CardContent className="space-y-3">
                    {documentTemplates.map((doc, i) => (
                      <Card key={i} className="rounded-2xl bg-brand-white shadow-lg transition-all hover:scale-[1.02] hover:shadow-xl">
                        <CardHeader className="pb-3">
                          <div className="flex items-center justify-between">
                            <div className="flex items-center space-x-3">
                              <div className="flex h-10 w-10 items-center justify-center rounded-xl bg-gradient-to-br from-brand-blue to-[#0066FF] shadow-md">
                                <FileText className="h-5 w-5 text-brand-white" />
                              </div>
                              <div>
                                <CardTitle className="text-sm font-bold text-brand-black">{doc.name}</CardTitle>
                                <CardDescription className="text-xs font-medium text-brand-black">{doc.access} access</CardDescription>
                              </div>
                            </div>
                            <Button size="sm" className="rounded-xl bg-gradient-to-r from-brand-blue to-[#0066FF] font-bold shadow-lg">Download</Button>
                          </div>
                        </CardHeader>
                      </Card>
                    ))}
                  </CardContent>
                </Card>
              </div>
            </TabsContent>

            {/* AI-Guide Demo */}
            <TabsContent value="ai-guide">
              <div className="grid gap-8 lg:grid-cols-2">
                <div>
                  <h3 className="mb-4 bg-gradient-to-r from-brand-blue to-[#0066FF] bg-clip-text text-3xl font-extrabold text-brand-black">AI-Guide</h3>
                  <p className="mb-6 text-lg font-medium text-brand-black">
                    Admins publish News & Updates and General Info once — UniAI answers questions about it instantly, right inside the chat.
                  </p>
                  <div className="relative">
                    <div className="absolute -inset-4 rounded-3xl bg-gradient-to-r from-brand-blue to-[#0066FF] opacity-20 blur-2xl"></div>
                    <img src={eLibraryPreview} alt="AI-Guide" className="relative rounded-3xl shadow-2xl" />
                  </div>
                </div>
                <Card className="rounded-3xl bg-brand-white shadow-xl">
                  <CardHeader>
                    <CardTitle className="bg-gradient-to-r from-brand-blue to-[#0066FF] bg-clip-text text-xl font-bold text-brand-black">UniAI — Guide Mode</CardTitle>
                    <CardDescription className="font-medium text-brand-black">Answers sourced from published Admin content</CardDescription>
                  </CardHeader>
                  <CardContent>
                    <ScrollArea className="h-[220px] rounded-2xl bg-gray-50 p-4 shadow-inner">
                      <div className="space-y-4">
                        {guideChat.map((msg, i) => (
                          <div key={i} className={`flex ${msg.from === 'user' ? 'justify-end' : 'justify-start'}`}>
                            <div className={`flex max-w-[85%] items-start space-x-2 ${msg.from === 'user' ? 'flex-row-reverse space-x-reverse' : ''}`}>
                              <div className={`flex h-8 w-8 flex-shrink-0 items-center justify-center rounded-full shadow-md ${msg.from === 'ai' ? 'bg-gradient-to-br from-brand-blue to-[#0066FF]' : 'bg-brand-yellow'}`}>
                                {msg.from === 'ai'
                                  ? <HelpCircle className="h-4 w-4 text-white" />
                                  : <span className="text-xs font-extrabold text-brand-black">U</span>
                                }
                              </div>
                              <div className={`rounded-2xl px-4 py-2.5 shadow-md ${msg.from === 'user' ? 'bg-gradient-to-r from-brand-blue to-[#0066FF] text-brand-white' : 'bg-brand-white text-brand-black'}`}>
                                <p className="whitespace-pre-line text-sm font-medium">{msg.text}</p>
                              </div>
                            </div>
                          </div>
                        ))}
                      </div>
                    </ScrollArea>
                    <div className="mt-4 flex space-x-2">
                      <Input placeholder="Ask UniAI anything..." className="rounded-xl font-medium shadow-md" />
                      <Button size="icon" className="rounded-xl bg-gradient-to-r from-brand-blue to-[#0066FF] shadow-lg">
                        <Send className="h-4 w-4" />
                      </Button>
                    </div>
                  </CardContent>
                </Card>
              </div>
            </TabsContent>

            {/* Groups & Channels Demo */}
            <TabsContent value="groups-channels">
              <div className="grid gap-8 lg:grid-cols-2">
                <div>
                  <h3 className="mb-4 bg-gradient-to-r from-brand-blue to-[#0066FF] bg-clip-text text-3xl font-extrabold text-brand-black">Groups & Channels</h3>
                  <p className="mb-6 text-lg font-medium text-brand-black">
                    WhatsApp-style group chats and topic channels, plus one-way broadcast Super Groups for large-scale announcements.
                  </p>
                  <div className="relative">
                    <div className="absolute -inset-4 rounded-3xl bg-gradient-to-r from-brand-blue to-[#0066FF] opacity-20 blur-2xl"></div>
                    <img src={messagingDemo} alt="Groups & Channels" className="relative rounded-3xl shadow-2xl" />
                  </div>
                </div>
                <Card className="rounded-3xl bg-brand-white shadow-xl">
                  <CardHeader>
                    <CardTitle className="bg-gradient-to-r from-brand-blue to-[#0066FF] bg-clip-text text-xl font-bold text-brand-black">Your Groups</CardTitle>
                    <CardDescription className="font-medium text-brand-black">Groups and broadcast-only Super Groups</CardDescription>
                  </CardHeader>
                  <CardContent className="space-y-4">
                    {superGroupsList.map((group, i) => (
                      <Card key={i} className="rounded-2xl bg-brand-white shadow-lg transition-all hover:scale-105 hover:shadow-xl">
                        <CardHeader>
                          <div className="flex items-center justify-between">
                            <div className="flex items-center space-x-3">
                              <div className="flex h-10 w-10 items-center justify-center rounded-xl bg-gradient-to-br from-brand-blue to-[#0066FF] shadow-md">
                                <MessagesSquare className="h-5 w-5 text-brand-white" />
                              </div>
                              <div>
                                <CardTitle className="text-sm font-bold text-brand-black">{group.name}</CardTitle>
                                <CardDescription className="text-xs font-medium text-brand-black">{group.members} members</CardDescription>
                              </div>
                            </div>
                            <span className={`rounded-full px-2 py-0.5 text-[10px] font-bold ${group.type === 'Super Group' ? 'bg-brand-yellow text-brand-black' : 'bg-brand-blue/10 text-brand-blue'}`}>
                              {group.type}
                            </span>
                          </div>
                        </CardHeader>
                      </Card>
                    ))}
                  </CardContent>
                </Card>
              </div>
            </TabsContent>

            {/* Notes Demo */}
            <TabsContent value="notes">
              <div className="grid gap-8 lg:grid-cols-2">
                <div>
                  <h3 className="mb-4 bg-gradient-to-r from-brand-blue to-[#0066FF] bg-clip-text text-3xl font-extrabold text-brand-black">Notes</h3>
                  <p className="mb-6 text-lg font-medium text-brand-black">
                    Upload, search, and share study notes across your institution — a shared resource, not tied to any one classroom.
                  </p>
                  <div className="relative">
                    <div className="absolute -inset-4 rounded-3xl bg-gradient-to-r from-brand-blue to-[#0066FF] opacity-20 blur-2xl"></div>
                    <img src={fileManagerDemo} alt="Notes" className="relative rounded-3xl shadow-2xl" />
                  </div>
                </div>
                <Card className="rounded-3xl bg-brand-white shadow-xl">
                  <CardHeader>
                    <CardTitle className="bg-gradient-to-r from-brand-blue to-[#0066FF] bg-clip-text text-xl font-bold text-brand-black">Shared Notes</CardTitle>
                    <CardDescription className="font-medium text-brand-black">Search by subject or keyword</CardDescription>
                  </CardHeader>
                  <CardContent className="space-y-3">
                    {notesList.map((note, i) => (
                      <Card key={i} className="rounded-2xl bg-brand-white shadow-lg transition-all hover:scale-[1.02] hover:shadow-xl">
                        <CardHeader className="pb-3">
                          <div className="flex items-center space-x-3">
                            <div className="flex h-10 w-10 items-center justify-center rounded-xl bg-gradient-to-br from-brand-blue to-[#0066FF] shadow-md">
                              <StickyNote className="h-5 w-5 text-brand-white" />
                            </div>
                            <div>
                              <CardTitle className="text-sm font-bold text-brand-black">{note.title}</CardTitle>
                              <CardDescription className="text-xs font-medium text-brand-black">{note.subject} · {note.by}</CardDescription>
                            </div>
                          </div>
                        </CardHeader>
                      </Card>
                    ))}
                  </CardContent>
                </Card>
              </div>
            </TabsContent>

            {/* Access Control Demo */}
            <TabsContent value="access-control">
              <div className="grid gap-8 lg:grid-cols-2">
                <div>
                  <h3 className="mb-4 bg-gradient-to-r from-brand-blue to-[#0066FF] bg-clip-text text-3xl font-extrabold text-brand-black">Access Control & Roles</h3>
                  <p className="mb-6 text-lg font-medium text-brand-black">
                    A clear permission hierarchy — from Super Admin down to Faculty and Librarian — keeping sensitive actions properly gated as your institution grows.
                  </p>
                  <div className="relative">
                    <div className="absolute -inset-4 rounded-3xl bg-gradient-to-r from-brand-blue to-[#0066FF] opacity-20 blur-2xl"></div>
                    <img src={facultyNetworkDemo} alt="Access Control" className="relative rounded-3xl shadow-2xl" />
                  </div>
                </div>
                <Card className="rounded-3xl bg-brand-white shadow-xl">
                  <CardHeader>
                    <CardTitle className="bg-gradient-to-r from-brand-blue to-[#0066FF] bg-clip-text text-xl font-bold text-brand-black">Role Hierarchy</CardTitle>
                    <CardDescription className="font-medium text-brand-black">Every role, scoped to what it needs</CardDescription>
                  </CardHeader>
                  <CardContent className="space-y-3">
                    {rolesList.map((r, i) => (
                      <Card key={i} className="rounded-2xl bg-brand-white shadow-lg transition-all hover:scale-[1.02] hover:shadow-xl">
                        <CardHeader className="pb-3">
                          <div className="flex items-center space-x-3">
                            <div className="flex h-10 w-10 items-center justify-center rounded-xl bg-gradient-to-br from-brand-blue to-[#0066FF] shadow-md">
                              <ShieldCheck className="h-5 w-5 text-brand-white" />
                            </div>
                            <div>
                              <CardTitle className="text-sm font-bold text-brand-black">{r.role}</CardTitle>
                              <CardDescription className="text-xs font-medium text-brand-black">{r.desc}</CardDescription>
                            </div>
                          </div>
                        </CardHeader>
                      </Card>
                    ))}
                  </CardContent>
                </Card>
              </div>
            </TabsContent>

          </Tabs>
        </div>
      </section>
    </div>
  );
}
